/**
 * Inventory Management tab model.
 *
 * Stock, Parts needs, Purchase orders and Transit warehouse are consolidated
 * into a single Inventory page with URL-driven tabs (?tab=…). These helpers
 * keep the visible tab set and the active tab permission-aware so a crafted
 * ?tab= value can never leave the MUI Tabs component on a tab the user is not
 * allowed to see (which would render a blank content area).
 */

export type InventoryTab =
  | 'stock'
  | 'needs'
  | 'purchaseorders'
  | 'returns'
  | 'warranty'
  | 'transit'
  | 'bundles'
  | 'stocktake'
  | 'costing'
  | 'pricing'
  | 'reports'
  | 'suppliers';

export interface InventoryTabPermissions {
  canReadInventory: boolean;
  canViewPurchaseOrders: boolean;
  /**
   * May read the opening-cost workbench: `inventory.read` OR `inventory.valuation.confirm` (the costing endpoints
   * answer both; whoever may confirm a cost may also see the layers to confirm). Omitted: same as `canReadInventory`.
   */
  canReadCosting?: boolean;
}

/**
 * Tabs the user may see, in canonical display order:
 * Stock · Parts needs · Purchase orders · Returns · Warranty claims ·
 * Transit warehouse · Part bundles · Stocktake · Costing · Pricing · Reports · Suppliers.
 *
 * Stock, Parts needs, Transit warehouse, Part bundles, Stocktake, Pricing and Reports require `inventory.read`
 * (what each of them may do beyond reading is decided inside the workbench: pricing changes need
 * `inventory.pricing.manage`, cost and revenue report variants `reports.view_financial`). Costing (opening cost
 * confirmation, batch 8 T11) requires `inventory.read` OR `inventory.valuation.confirm`, so a user who may only
 * confirm opening costs still reaches it. Purchase orders, Returns (RMA), Warranty claims and Suppliers require
 * `purchase_orders.read` — they belong to the parts-ordering audience.
 * The page additionally hides the ordering tabs, including Suppliers, when
 * `partsOrderingEnabled` is off; Costing, Pricing and Reports are not ordering surfaces and stay.
 */
export function getAllowedInventoryTabs({
  canReadInventory,
  canViewPurchaseOrders,
  canReadCosting = canReadInventory,
}: InventoryTabPermissions): InventoryTab[] {
  const tabs: InventoryTab[] = [];
  if (canReadInventory) tabs.push('stock', 'needs');
  if (canViewPurchaseOrders) tabs.push('purchaseorders', 'returns', 'warranty');
  if (canReadInventory) tabs.push('transit', 'bundles', 'stocktake');
  if (canReadCosting) tabs.push('costing');
  if (canReadInventory) tabs.push('pricing', 'reports');
  if (canViewPurchaseOrders) tabs.push('suppliers');
  return tabs;
}

/**
 * Clamp a requested (URL) tab to one the user is actually allowed to see.
 * Falls back to the first allowed tab, or 'stock' when nothing is allowed.
 */
export function resolveInventoryTab(
  requested: string | null,
  allowed: InventoryTab[],
): InventoryTab {
  if (requested && (allowed as string[]).includes(requested)) {
    return requested as InventoryTab;
  }
  return allowed[0] ?? 'stock';
}
